import { Card } from "@/shared/components/ui/card"
import type { StatisticsDay, StatisticsRecommendation } from "./api/statistics.queries"
import {
  BARS,
  barsSvgHeight,
  formatDayLabel,
  labelStride,
  niceMaxHours,
  PLOT,
  pointX,
  valueToY,
} from "./statistics.config"

interface SleepBarsChartProps {
  days: StatisticsDay[]
  recommendation: StatisticsRecommendation
}

const BASELINE = BARS.topPad + BARS.plotHeight

// Total-sleep line + filled area across the whole range.
function areaPath(days: StatisticsDay[], maxHours: number): { line: string; area: string } {
  const pts = days.map(
    (d, i) => `${pointX(i, days.length)},${valueToY(d.nightMinutes + d.dayMinutes, maxHours)}`,
  )
  const line = `M${pts.join(" L")}`
  const area = `${line} L${pointX(days.length - 1, days.length)},${BASELINE} L${pointX(0, days.length)},${BASELINE} Z`
  return { line, area }
}

export function SleepBarsChart({ days, recommendation }: SleepBarsChartProps) {
  if (days.length === 0) {
    return (
      <Card className="items-center py-8 text-sm text-muted-foreground">
        Ingen sömn registrerad ännu
      </Card>
    )
  }

  const maxDailyMinutes = Math.max(...days.map((d) => d.nightMinutes + d.dayMinutes))
  const maxHours = niceMaxHours(maxDailyMinutes, recommendation.maxHours)
  const svgHeight = barsSvgHeight()
  const stride = labelStride(days.length)

  const bandTop = valueToY(recommendation.maxHours * 60, maxHours)
  const bandBottom = valueToY(recommendation.minHours * 60, maxHours)
  const gridHours = [0, maxHours / 2, maxHours]
  const { line, area } = areaPath(days, maxHours)

  return (
    <Card className="gap-2 py-4">
      <div className="flex items-center justify-between px-4">
        <h2 className="text-sm font-semibold">Sömn per dygn</h2>
        <span className="text-xs text-muted-foreground">timmar</span>
      </div>
      <div className="flex px-2">
        {/* Fixed y-axis; the plot flexes to fill the width. */}
        <svg
          width={BARS.leftAxis}
          height={svgHeight}
          className="shrink-0 text-muted-foreground"
          role="presentation"
        >
          {gridHours.map((h) => (
            <text
              key={h}
              x={BARS.leftAxis - 6}
              y={valueToY(h * 60, maxHours) + 4}
              textAnchor="end"
              className="fill-current text-xs"
            >
              {h}
            </text>
          ))}
        </svg>

        <svg
          viewBox={`0 0 ${PLOT.width} ${svgHeight}`}
          className="h-auto w-full"
          role="img"
          aria-label="Sömn per dygn"
        >
          <rect
            x={0}
            y={bandTop}
            width={PLOT.width}
            height={Math.max(0, bandBottom - bandTop)}
            className="fill-primary/10"
          />
          {gridHours.map((h) => (
            <line
              key={h}
              x1={0}
              x2={PLOT.width}
              y1={valueToY(h * 60, maxHours)}
              y2={valueToY(h * 60, maxHours)}
              className="stroke-border"
              strokeWidth={1}
            />
          ))}

          <path d={area} className="fill-primary/15" />
          <path
            d={line}
            fill="none"
            className="stroke-primary"
            strokeWidth={2}
            strokeLinecap="round"
            strokeLinejoin="round"
          />

          {days.map((day, i) =>
            i % stride === 0 ? (
              <text
                key={day.date}
                x={pointX(i, days.length)}
                y={svgHeight - 8}
                textAnchor="middle"
                className="fill-muted-foreground text-[9px]"
              >
                {formatDayLabel(day.date)}
              </text>
            ) : null,
          )}
        </svg>
      </div>
    </Card>
  )
}
